/**
 * Les libellés de la librairie — ce qu'elle écrit d'elle-même (« Fermer », « Annuler »,
 * « 1–12 sur 205 », « Tout cocher sur la page »…), en français par défaut, traduisibles d'un
 * coup, pour toutes les apps :
 *
 *   <Libelles valeurs={{ fermer: "Close", confirmer: "Confirm", annuler: "Cancel", pagination: { sur: "of" } }}>
 *     <App />
 *   </Libelles>
 *
 * Une prop explicite (`closeLabel`, `confirmer`, `sur`, `vide`…) l'emporte toujours sur le
 * contexte ; sans `<Libelles>`, c'est le français. La librairie ne dépend d'aucun i18n :
 * l'app traduit avec le sien et pose le résultat ici, une fois, à sa racine.
 *
 * Vague 2 (02-11-2026) : `Confirmer`, la `Pagination` du `Tableau` et le Tableau lui-même
 * avaient des mots figés en français ; les relecteurs voulaient qu'ils soient exigés ou
 * traduisibles proprement. C'est ceci.
 */
import { createContext, useContext, useMemo, type ReactNode } from "react";

export interface LibellesUi {
  /** La croix d'une fenêtre, d'un tiroir, d'un toast, d'une alerte. */
  fermer: string;
  /** Les deux boutons de `Confirmer`. */
  confirmer: string;
  annuler: string;
  pagination: {
    /** Le nom de la navigation (lecteur d'écran). */
    nom: string;
    precedent: string;
    suivant: string;
    /** Le mot entre les bornes et le total : « 1–12 sur 205 ». */
    sur: string;
  };
  tableau: {
    toutCocher: string;
    /** L'étiquette de la case d'une ligne : « Cocher Trista ». */
    cocher: (nom: string) => string;
    /** « sélectionné » / « sélectionnés », après le nombre. */
    selectionnes: (n: number) => string;
    toutDecocher: string;
    filtre: string;
    /** Le nom du panneau des filtres (lecteur d'écran). */
    filtres: string;
    total: string;
    vide: string;
    /** Le bouton qui ouvre la fiche d'une ligne quand des colonnes sont cachées, et le titre de cette fiche. */
    detail: string;
    oui: string;
    non: string;
  };
  delta: {
    /** Le mot de la pastille quand la période d'avant valait zéro. */
    nouveau: string;
    rienAvant: string;
    /** Après le pourcentage, dans l'infobulle : « +12 % vs la période d'avant ». */
    vs: string;
  };
}

export const LIBELLES_FR: LibellesUi = {
  fermer: "Fermer",
  confirmer: "Confirmer",
  annuler: "Annuler",
  pagination: { nom: "Pages", precedent: "Page précédente", suivant: "Page suivante", sur: "sur" },
  tableau: {
    toutCocher: "Tout cocher sur la page",
    cocher: (nom) => `Cocher ${nom}`,
    selectionnes: (n) => (n > 1 ? "sélectionnés" : "sélectionné"),
    toutDecocher: "Tout décocher",
    filtre: "Filtre",
    filtres: "Filtres",
    total: "Total",
    vide: "Rien à afficher",
    detail: "Détail",
    oui: "Oui",
    non: "Non",
  },
  delta: { nouveau: "Nouveau", rienAvant: "Rien sur la période d'avant", vs: "vs la période d'avant" },
};

/** Ce qu'on donne à `<Libelles>` : chaque mot est facultatif, groupe par groupe. */
export type LibellesPartiels = {
  [K in keyof LibellesUi]?: LibellesUi[K] extends string ? string : Partial<LibellesUi[K]>;
};

const Contexte = createContext<LibellesUi>(LIBELLES_FR);

function fondre(base: LibellesUi, valeurs: LibellesPartiels): LibellesUi {
  return {
    fermer: valeurs.fermer ?? base.fermer,
    confirmer: valeurs.confirmer ?? base.confirmer,
    annuler: valeurs.annuler ?? base.annuler,
    pagination: { ...base.pagination, ...valeurs.pagination },
    tableau: { ...base.tableau, ...valeurs.tableau },
    delta: { ...base.delta, ...valeurs.delta },
  };
}

/** Pose les libellés pour tout ce qu'il enveloppe ; emboîtable (un écran peut en changer un seul). */
export function Libelles({ valeurs, children }: { valeurs: LibellesPartiels; children: ReactNode }) {
  const parent = useContext(Contexte);
  const fondus = useMemo(() => fondre(parent, valeurs), [parent, valeurs]);
  return <Contexte.Provider value={fondus}>{children}</Contexte.Provider>;
}

/** Les libellés de l'endroit — pour un composant de la librairie, ou un écran qui veut les mêmes mots. */
export function useLibelles(): LibellesUi {
  return useContext(Contexte);
}
